/**
 * Keyframe tracks (audit item 11): per-channel `{t, v, ease?}` keyframes on a
 * clip, t normalized 0..1 over the clip duration, v in UI units (deg/%/px).
 * All operations are pure — the graph editor is a thin view over this module,
 * and the `keyframed` preset compiles tracks into GSAP segments.
 */
import { gsap } from "gsap";

import type { AnimatedValues } from "@excalidraw/element";

export type TrackKeyframe = {
  /** normalized time, 0..1 over the clip duration */
  t: number;
  /** value in the channel's UI units */
  v: number;
  /** GSAP ease name for the segment ARRIVING at this keyframe;
   * "hold" freezes the previous value until this key. Default linear. */
  ease?: string;
};

export type ClipTracks = Record<string, TrackKeyframe[]>;

export type TrackChannel = {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  /** rest value in UI units */
  neutral: number;
  /** curve color — fixed palette, matches the category-accent tones */
  color: string;
  /** writes a UI-unit value onto the renderer target */
  apply: (target: AnimatedValues, v: number) => void;
};

const rad = (deg: number) => (deg * Math.PI) / 180;

export const TRACK_CHANNELS: TrackChannel[] = [
  {
    key: "moveX",
    label: "Move X",
    min: -2000,
    max: 2000,
    step: 10,
    unit: "px",
    neutral: 0,
    color: "#5ab0d6",
    apply: (o, v) => (o.dx = v),
  },
  {
    key: "moveY",
    label: "Move Y",
    min: -2000,
    max: 2000,
    step: 10,
    unit: "px",
    neutral: 0,
    color: "#6fbf73",
    apply: (o, v) => (o.dy = v),
  },
  {
    key: "rotate",
    label: "Rotate",
    min: -1080,
    max: 1080,
    step: 15,
    unit: "°",
    neutral: 0,
    color: "#e0839f",
    apply: (o, v) => (o.angleDelta = rad(v)),
  },
  {
    key: "scaleX",
    label: "Scale X",
    min: 0,
    max: 4,
    step: 0.05,
    unit: "×",
    neutral: 1,
    color: "#d6b45a",
    apply: (o, v) => (o.scaleX = v),
  },
  {
    key: "scaleY",
    label: "Scale Y",
    min: 0,
    max: 4,
    step: 0.05,
    unit: "×",
    neutral: 1,
    color: "#bfa07a",
    apply: (o, v) => (o.scaleY = v),
  },
  {
    key: "skewX",
    label: "Skew X",
    min: -60,
    max: 60,
    step: 1,
    unit: "°",
    neutral: 0,
    color: "#8a7fd6",
    apply: (o, v) => (o.skewX = rad(v)),
  },
  {
    key: "skewY",
    label: "Skew Y",
    min: -60,
    max: 60,
    step: 1,
    unit: "°",
    neutral: 0,
    color: "#7a8fd6",
    apply: (o, v) => (o.skewY = rad(v)),
  },
  {
    key: "opacity",
    label: "Opacity",
    min: 0,
    max: 100,
    step: 5,
    unit: "%",
    neutral: 100,
    color: "#8e8e93",
    apply: (o, v) => (o.opacity = v / 100),
  },
  {
    key: "blur",
    label: "Blur",
    min: 0,
    max: 50,
    step: 1,
    unit: "px",
    neutral: 0,
    color: "#5a9bd6",
    apply: (o, v) => (o.blur = v),
  },
  {
    key: "hue",
    label: "Hue",
    min: -360,
    max: 360,
    step: 10,
    unit: "°",
    neutral: 0,
    color: "#c77dd6",
    apply: (o, v) => (o.hueRotate = v),
  },
  {
    key: "brightness",
    label: "Brightness",
    min: 0,
    max: 4,
    step: 0.1,
    unit: "×",
    neutral: 1,
    color: "#d98a5f",
    apply: (o, v) => (o.brightness = v),
  },
  {
    key: "contrast",
    label: "Contrast",
    min: 0.2,
    max: 4,
    step: 0.1,
    unit: "×",
    neutral: 1,
    color: "#a3a35a",
    apply: (o, v) => (o.contrast = v),
  },
  {
    key: "saturate",
    label: "Saturate",
    min: 0,
    max: 4,
    step: 0.1,
    unit: "×",
    neutral: 1,
    color: "#5ac8b8",
    apply: (o, v) => (o.saturate = v),
  },
  {
    key: "sepia",
    label: "Sepia",
    min: 0,
    max: 1,
    step: 0.1,
    unit: "",
    neutral: 0,
    color: "#b8a58a",
    apply: (o, v) => (o.sepia = v),
  },
];

const CHANNELS_BY_KEY = new Map(TRACK_CHANNELS.map((c) => [c.key, c]));

export const trackChannel = (key: string): TrackChannel | undefined =>
  CHANNELS_BY_KEY.get(key);

/** Segment eases offered per keyframe — "hold" is the cel-animation step. */
export const KEYFRAME_EASES: readonly string[] = [
  "linear",
  "power1.inOut",
  "power2.out",
  "power3.inOut",
  "back.out",
  "elastic.out",
  "bounce.out",
  "steps(4)",
  "bezier(0.5,0,0.5,1)",
  "hold",
];

/**
 * Cubic-bezier ease (CSS-style control points): solves x(t) = p with
 * Newton–Raphson (bisection fallback) and returns y(t) — the audit's
 * missing "Newton-Raphson bezier solver". x1/x2 are clamped to [0,1] so the
 * curve stays a function of time; y may overshoot.
 */
export const bezierEase = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): ((p: number) => number) => {
  const cx1 = Math.max(0, Math.min(1, x1));
  const cx2 = Math.max(0, Math.min(1, x2));
  const cubic = (t: number, a: number, b: number) =>
    3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
  const cubicDeriv = (t: number, a: number, b: number) =>
    3 * a * (1 - t) * (1 - t) + 6 * (b - a) * t * (1 - t) + 3 * (1 - b) * t * t;
  return (p: number) => {
    if (p <= 0) {
      return 0;
    }
    if (p >= 1) {
      return 1;
    }
    let t = p;
    for (let i = 0; i < 8; i++) {
      const err = cubic(t, cx1, cx2) - p;
      if (Math.abs(err) < 1e-6) {
        break;
      }
      const d = cubicDeriv(t, cx1, cx2);
      if (Math.abs(d) < 1e-6) {
        break;
      }
      t = Math.max(0, Math.min(1, t - err / d));
    }
    // bisection backstop for flat-derivative regions
    if (Math.abs(cubic(t, cx1, cx2) - p) > 1e-4) {
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 24; i++) {
        t = (lo + hi) / 2;
        if (cubic(t, cx1, cx2) < p) {
          lo = t;
        } else {
          hi = t;
        }
      }
    }
    return cubic(t, y1, y2);
  };
};

const BEZIER_RE =
  /^bezier\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)$/;

/** The four control values of a `bezier(x1,y1,x2,y2)` ease string, or null. */
export const parseBezier = (
  ease: string,
): [number, number, number, number] | null => {
  const m = BEZIER_RE.exec(ease);
  if (!m) {
    return null;
  }
  const values = [m[1], m[2], m[3], m[4]].map(Number);
  return values.every(Number.isFinite)
    ? (values as [number, number, number, number])
    : null;
};

/** A track ease resolved for GSAP/sampling: a GSAP ease name, "none" for
 * linear/absent/malformed, or a raw function for bezier strings (GSAP
 * accepts function eases). */
export const resolveTrackEase = (
  ease: string | undefined,
): string | ((p: number) => number) => {
  if (!ease || ease === "linear") {
    return "none";
  }
  if (ease.startsWith("bezier(")) {
    const parsed = parseBezier(ease);
    return parsed ? bezierEase(...parsed) : "none";
  }
  return ease;
};

/** Two keyframes closer than this in normalized time collapse into one. */
const T_EPSILON = 0.001;

const sortByT = (kfs: TrackKeyframe[]) => [...kfs].sort((a, b) => a.t - b.t);

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

/** Inserts a keyframe (sorted); a near-duplicate time replaces in place. */
export const addKeyframe = (
  tracks: ClipTracks,
  channel: string,
  t: number,
  v: number,
): ClipTracks => {
  const ch = trackChannel(channel);
  const kf: TrackKeyframe = {
    t: clamp(t, 0, 1),
    v: ch ? clamp(v, ch.min, ch.max) : v,
  };
  const existing = tracks[channel] ?? [];
  const withoutDupe = existing.filter((k) => Math.abs(k.t - kf.t) >= T_EPSILON);
  return { ...tracks, [channel]: sortByT([...withoutDupe, kf]) };
};

/** Patches one keyframe; clamps t/v and re-sorts when the time moved. */
export const updateKeyframe = (
  tracks: ClipTracks,
  channel: string,
  index: number,
  patch: Partial<TrackKeyframe>,
): ClipTracks => {
  const kfs = tracks[channel];
  if (!kfs || !kfs[index]) {
    return tracks;
  }
  const ch = trackChannel(channel);
  const next = kfs.map((k, i) => {
    if (i !== index) {
      return k;
    }
    const merged = { ...k, ...patch };
    merged.t = clamp(merged.t, 0, 1);
    if (ch) {
      merged.v = clamp(merged.v, ch.min, ch.max);
    }
    return merged;
  });
  return { ...tracks, [channel]: sortByT(next) };
};

/** Removes a keyframe; a channel whose last keyframe goes is dropped. */
export const removeKeyframe = (
  tracks: ClipTracks,
  channel: string,
  index: number,
): ClipTracks => {
  const kfs = tracks[channel];
  if (!kfs) {
    return tracks;
  }
  const next = kfs.filter((_, i) => i !== index);
  if (next.length === 0) {
    const { [channel]: _gone, ...rest } = tracks;
    return rest;
  }
  return { ...tracks, [channel]: next };
};

/** Eased value at normalized time t — the same semantics the compiled GSAP
 * segments produce, used for curve preview and add-on-curve insertion. */
export const sampleTrack = (kfs: TrackKeyframe[], t: number): number => {
  if (kfs.length === 0) {
    return 0;
  }
  if (t <= kfs[0].t) {
    return kfs[0].v;
  }
  const last = kfs[kfs.length - 1];
  if (t >= last.t) {
    return last.v;
  }
  for (let i = 1; i < kfs.length; i++) {
    if (t <= kfs[i].t) {
      const a = kfs[i - 1];
      const b = kfs[i];
      if (b.ease === "hold") {
        return t < b.t ? a.v : b.v;
      }
      const span = b.t - a.t || 1;
      const p = (t - a.t) / span;
      const resolved = resolveTrackEase(b.ease);
      const easeFn =
        typeof resolved === "function"
          ? resolved
          : resolved !== "none"
          ? gsap.parseEase(resolved)
          : null;
      return a.v + (b.v - a.v) * (easeFn ? easeFn(p) : p);
    }
  }
  return last.v;
};

export const isValidTracks = (value: unknown): value is ClipTracks => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  for (const [channel, kfs] of Object.entries(value)) {
    if (!CHANNELS_BY_KEY.has(channel) || !Array.isArray(kfs)) {
      return false;
    }
    let prevT = -Infinity;
    for (const kf of kfs) {
      if (typeof kf !== "object" || kf === null) {
        return false;
      }
      const { t, v, ease } = kf as Record<string, unknown>;
      if (
        typeof t !== "number" ||
        !Number.isFinite(t) ||
        t < 0 ||
        t > 1 ||
        t < prevT ||
        typeof v !== "number" ||
        !Number.isFinite(v) ||
        (ease !== undefined && typeof ease !== "string")
      ) {
        return false;
      }
      prevT = t;
    }
  }
  return true;
};
